import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Slide,
    Box,
    Typography,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import CloseIcon from '@mui/icons-material/Close';
import { PlusIcon } from '../../../Components/Layouts/CustomIcons';
import SuccessNotification from '../../../Components/Common/SuccessNotification';
import CheckIcon from '@mui/icons-material/Check';
import { addCategory, deleteCategoryName, updateCategoryName } from '../../../Services/authService';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmationPopup from '../../../Components/Common/DeleteConfirmationPopup';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function CategoryModal({ open, categories, onClose, fetchCategories }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const [successNotification, setSuccessNotification] = useState(false);
    const [addCategoryOpen, setAddCategoryOpen] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [errors, setErrors] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Ponytail: single reset to prevent stale state on reopen
    const resetFormState = () => {
        setAddCategoryOpen(false);
        setGeneralError('');
        setCategoryName('');
        setErrors('');
        setEditingCategoryId(null);
        setEditingCategoryName('');
        setIsDeletePopupOpen(false);
        setLoading(false);
        setSelectedCategory(null);
        setErrorMessage('');
        setSuccessMessage('');
        setNotificationOpen(false);
        setSearchOpen(false);
        setSearchQuery('');
    };

    const handleClose = () => {
        resetFormState();
        onClose();
    };

    const modalBackgrounColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const borderLineColor = mode === 'dark' ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;
    const textHeadColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
    const textColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

    const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

    const handleSave = async () => {
        if (!categoryName.trim()) {
            // Store the key, translate at render — keeps the message in sync
            // when the language changes while the error is visible
            setErrors('inventory.enterCategoryName');
            return;
        }
        if (categoryName.trim().length < 2) {
            setErrors('coupons.nameMinLength');
            return;
        }
        try {
            const categoryData = { name: categoryName };
            await addCategory(categoryData);
            setSuccessNotification(true);
            fetchCategories();
            setCategoryName('');
            setAddCategoryOpen(false);
        } catch (error) {
            setGeneralError(error?.response?.data?.detail);
            console.error(t('inventory.errorCreatingProduct'), error);
        }
    };

    const handleUpdate = async (id) => {
        if (!editingCategoryName.trim()) {
            setErrors('inventory.enterCategoryName');
            return;
        }
        if (editingCategoryName.trim().length < 2) {
            setErrors('coupons.nameMinLength');
            return;
        }
        try {
            await updateCategoryName(id, editingCategoryName);
            setSuccessNotification(true);
            fetchCategories();
            setEditingCategoryId(null);
            setEditingCategoryName('');
        } catch (error) {
            setGeneralError(error?.response?.data?.detail);
            console.error(t('inventory.errorUpdatingCategory'), error);
        }
    };


    const handleDeleteClick = (category) => {
        setSelectedCategory(category)
        setErrorMessage(''); // Clear any error from a previous delete attempt
        setIsDeletePopupOpen(true);
    };

    const handleMenuOpen = (event, category) => {
        event.stopPropagation();
        // Close search and add category when opening edit mode
        setSearchOpen(false);
        setSearchQuery('');
        setAddCategoryOpen(false);
        setCategoryName('');
        setErrors('');

        setEditingCategoryId(category.id);
        setEditingCategoryName(category.name);
    };

    const handleCloseDeletePopup = () => {
        setIsDeletePopupOpen(false);
        setErrorMessage(''); // Clear error so it doesn't persist on reopen
    };

    const handleCloseNotification = () => {
        setNotificationOpen(false);
        setSuccessMessage('');
    };

    const handleConfirmDelete = async () => {
        if (!selectedCategory) return;

        try {
            setLoading(true)
            await deleteCategoryName(selectedCategory.id); // Call the delete API
            setSuccessMessage(t('inventory.CategoryDeleted'));
            setNotificationOpen(true); // Show success notification
            handleCloseDeletePopup(); // Close the delete confirmation popup
            fetchCategories();
        } catch (error) {
            setErrorMessage(t('inventory.errorDeletingCategory'));
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        right: 10,
                        margin: 0,
                        borderRadius: 3,
                        background: modalBackgrounColor,
                        width: "97%",
                        "@media (max-width: 427px)": {
                            right: 5,
                        },
                    },
                }}
            >
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",
                    }
                }}>                    <span className='franchise-heading-text'>
                        {t('inventory.categories')}
                    </span>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', position: 'relative' }}>
                            <TextField
                                placeholder={t('inventory.searchCategories')}
                                variant="standard"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    // Handle keyboard navigation
                                    if (e.key === 'Escape') {
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                    }
                                    // When search is opened, close edit and add modes
                                    if (!searchOpen && e.key === 'Enter') {
                                        setEditingCategoryId(null);
                                        setEditingCategoryName('');
                                        setAddCategoryOpen(false);
                                        setCategoryName('');
                                        setErrors('');
                                    }
                                }}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (
                                        <SearchIcon sx={{ color: textHeadColor, mr: 1, fontSize: 20 }} />
                                    ),
                                    endAdornment: searchQuery && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchQuery('')}
                                            sx={{ p: 0.5, }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    ),
                                }}
                                sx={{
                                    width: searchOpen ? '175px' : '0px',
                                    transition: 'width 0.3s ease-in-out',
                                    input: { color: textHeadColor, fontSize: 14, padding: '4px 0' },
                                    '& .MuiInputBase-root': { backgroundColor: 'transparent' },
                                    mr: searchOpen ? 1 : 0,
                                    opacity: searchOpen ? 1 : 0,
                                }}
                                inputRef={searchOpen ? (input) => input?.focus() : null}
                            />
                        </Box>

                        {!searchOpen ? (
                            <IconButton
                                onClick={() => {
                                    // Close edit and add modes when opening search
                                    setEditingCategoryId(null);
                                    setEditingCategoryName('');
                                    setAddCategoryOpen(false);
                                    setCategoryName('');
                                    setErrors('');
                                    setSearchOpen(true);
                                }}
                                aria-label="search"
                                sx={{ color: textHeadColor }}
                            >
                                <SearchIcon />
                            </IconButton>
                        ) : (
                            <IconButton
                                onClick={() => {
                                    setSearchOpen(false);
                                    setSearchQuery('');
                                }}
                                aria-label="close-search"
                                sx={{ color: textHeadColor }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rotate-ccw-icon lucide-rotate-ccw"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                            </IconButton>
                        )}


                        <IconButton onClick={handleClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '80vh',
                    // maxHeight: '600px',
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",
                    },
                    overflow: 'hidden',


                    //   const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

                    //   const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

                }}>
                    {/* Scrollable category list */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        mb: 2,
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            borderRadius: '3px',
                            '&:hover': {
                                backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                            }
                        },
                        // marginRight: 1,
                        '&::-webkit-scrollbar': {
                            width: '8px !important',
                            height: '8px !important',
                            padding: '2px !important',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: scroll1,
                            borderRadius: '10px',
                            border: `1px solid ${scroll}`,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: scroll,
                            borderRadius: '10px',
                            width: '3px !important',
                            border: '3px solid transparent',
                            backgroundClip: 'padding-box',
                            '&:hover': {
                                backgroundColor: scroll,
                            },
                        },
                    }}>
                        {categories && categories.length > 0 ? (
                            categories
                                .filter((category) => {
                                    if (!searchQuery.trim()) return true;
                                    const searchQueryLower = searchQuery.toLowerCase().trim();
                                    const categoryName = category.name.toLowerCase();

                                    // Strategy 1: Try exact match with the full search query (handles "week19" matching "Week 19")
                                    const categoryNameNoSpaces = categoryName.replace(/\s+/g, '');
                                    const searchQueryNoSpaces = searchQueryLower.replace(/\s+/g, '');
                                    if (categoryNameNoSpaces.includes(searchQueryNoSpaces)) return true;

                                    // Strategy 2: Try matching with spaces in search query (handles "week 19" matching "Week 19")
                                    if (categoryName.includes(searchQueryLower)) return true;

                                    // Strategy 3: Split search terms and require all to be present (handles multi-word searches)
                                    const searchTerms = searchQueryLower.split(/\s+/);
                                    return searchTerms.every(term => categoryName.includes(term));
                                })
                                .map((category) => {
                                    // Highlight matching text - handle multiple search terms
                                    const highlightText = (text, query) => {
                                        if (!query.trim()) return text;

                                        const searchQueryLower = query.toLowerCase().trim();
                                        const lowerText = text.toLowerCase();
                                        let result = [];
                                        let lastIndex = 0;

                                        // Find all matching positions using multiple strategies
                                        const matches = [];

                                        // Strategy 1: Match full query without spaces (handles "week19" in "Week 19")
                                        const textNoSpaces = lowerText.replace(/\s+/g, '');
                                        const queryNoSpaces = searchQueryLower.replace(/\s+/g, '');
                                        if (queryNoSpaces.length > 0) {
                                            let startIndex = 0;
                                            while (true) {
                                                const index = textNoSpaces.indexOf(queryNoSpaces, startIndex);
                                                if (index === -1) break;
                                                // Map back to original positions (accounting for removed spaces)
                                                let originalStart = 0;
                                                let spaceCount = 0;
                                                for (let i = 0; i < index; i++) {
                                                    if (lowerText[originalStart] === ' ') spaceCount++;
                                                    originalStart++;
                                                }
                                                const originalEnd = originalStart + queryNoSpaces.length + spaceCount;
                                                matches.push({ start: originalStart, end: Math.min(originalEnd, text.length) });
                                                startIndex = index + 1;
                                            }
                                        }

                                        // Strategy 2: Match full query with spaces (handles "week 19" in "Week 19")
                                        if (searchQueryLower.length > 0) {
                                            let startIndex = 0;
                                            while (true) {
                                                const index = lowerText.indexOf(searchQueryLower, startIndex);
                                                if (index === -1) break;
                                                matches.push({ start: index, end: index + searchQueryLower.length });
                                                startIndex = index + 1;
                                            }
                                        }

                                        // Strategy 3: Match individual terms (handles multi-word searches)
                                        const searchTerms = searchQueryLower.split(/\s+/).filter(term => term.length > 0);
                                        searchTerms.forEach(term => {
                                            let startIndex = 0;
                                            while (true) {
                                                const index = lowerText.indexOf(term, startIndex);
                                                if (index === -1) break;
                                                matches.push({ start: index, end: index + term.length });
                                                startIndex = index + 1;
                                            }
                                        });

                                        // Sort and merge overlapping matches
                                        matches.sort((a, b) => a.start - b.start);
                                        const mergedMatches = [];
                                        for (const match of matches) {
                                            if (mergedMatches.length === 0 || match.start > mergedMatches[mergedMatches.length - 1].end) {
                                                mergedMatches.push({ ...match });
                                            } else {
                                                mergedMatches[mergedMatches.length - 1].end = Math.max(
                                                    mergedMatches[mergedMatches.length - 1].end,
                                                    match.end
                                                );
                                            }
                                        }

                                        // Build highlighted result
                                        for (const match of mergedMatches) {
                                            if (match.start > lastIndex) {
                                                result.push(text.substring(lastIndex, match.start));
                                            }
                                            result.push(
                                                <strong key={match.start} style={{ fontWeight: 700 }}>
                                                    {text.substring(match.start, match.end)}
                                                </strong>
                                            );
                                            lastIndex = match.end;
                                        }

                                        if (lastIndex < text.length) {
                                            result.push(text.substring(lastIndex));
                                        }

                                        return result.length > 0 ? result : text;
                                    };

                                    return (
                                        <Box
                                            key={category.id}
                                            sx={{
                                                display: 'flex',
                                                cursor: 'default',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '20px 0',
                                                borderBottom: `1px solid ${borderLineColor}`
                                            }}
                                        >
                                            {editingCategoryId === category.id ? (
                                                <>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                        <TextField
                                                            value={editingCategoryName}
                                                            onChange={(e) => setEditingCategoryName(e.target.value)}
                                                            variant="standard"
                                                            fullWidth
                                                            helperText={errors && t(errors)}
                                                            InputProps={{
                                                                disableUnderline: true,
                                                            }}
                                                            sx={{
                                                                input: { color: textHeadColor },
                                                                '& .MuiInputBase-root': { backgroundColor: 'transparent' },
                                                            }}
                                                        />
                                                        <IconButton onClick={() => handleUpdate(category.id)} sx={{ color: '#4caf50' }}>
                                                            <CheckIcon />
                                                        </IconButton>
                                                        <IconButton onClick={() => {
                                                            setEditingCategoryId(null);
                                                            setEditingCategoryName('');
                                                            setErrors('');
                                                        }} sx={{ color: '#f44336' }}>
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Box>
                                                    <IconButton
                                                        onClick={(e) => handleDeleteClick(category)}
                                                        sx={{ color: '#f44336', ml: 1 }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        className='popup-text'
                                                        style={{ margin: '0px', color: textHeadColor }}
                                                    >
                                                        {highlightText(category.name, searchQuery)}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography
                                                            className='add-machine'
                                                            style={{ margin: '0px', color: textColor }}
                                                        >
                                                            {category.productCount} {t('inventory.products')}
                                                        </Typography>
                                                        <IconButton
                                                            onClick={(e) => handleMenuOpen(e, category)}
                                                            size="small"
                                                            sx={{
                                                                color: textHeadColor,
                                                                padding: '4px',
                                                                '&:hover': {
                                                                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                                                                }
                                                            }}
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    );
                                })
                        ) : (
                            <Typography variant="body2" color="textSecondary">{t('inventory.NoCategoryFound')}</Typography>
                        )}
                        {searchQuery && categories && categories.length > 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 2 }}>
                                {categories.filter((category) => {
                                    if (!searchQuery.trim()) return true;
                                    const searchQueryLower = searchQuery.toLowerCase().trim();
                                    const categoryName = category.name.toLowerCase();

                                    // Strategy 1: Try exact match with the full search query
                                    const categoryNameNoSpaces = categoryName.replace(/\s+/g, '');
                                    const searchQueryNoSpaces = searchQueryLower.replace(/\s+/g, '');
                                    if (categoryNameNoSpaces.includes(searchQueryNoSpaces)) return true;

                                    // Strategy 2: Try matching with spaces in search query
                                    if (categoryName.includes(searchQueryLower)) return true;

                                    // Strategy 3: Split search terms and require all to be present
                                    const searchTerms = searchQueryLower.split(/\s+/);
                                    return searchTerms.every(term => categoryName.includes(term));
                                }).length === 0 && t('inventory.noMatchingCategories')}
                            </Typography>
                        )}
                    </Box>

                    {/* Fixed Add Category button */}
                    <Box sx={{
                        flexShrink: 0,
                    }}>
                        <Box>
                            {addCategoryOpen ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: `1px solid ${borderLineColor}`,
                                    }}
                                >
                                    <TextField
                                        value={categoryName}
                                        onChange={(e) => setCategoryName(e.target.value)}
                                        placeholder={t('inventory.categoryName')}
                                        variant="standard"
                                        fullWidth
                                        helperText={errors && t(errors)}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        sx={{
                                            input: { color: textHeadColor },
                                            '& .MuiInputBase-root': { backgroundColor: 'transparent' },
                                        }}
                                    />
                                    <IconButton onClick={handleSave} sx={{ color: '#4caf50' }}>
                                        <CheckIcon />
                                    </IconButton>
                                    <IconButton onClick={() => {
                                        setAddCategoryOpen(false);
                                        setCategoryName('');
                                        setErrors('');
                                    }} sx={{ color: '#f44336' }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box
                                    onClick={() => {
                                        // Close search and edit modes when opening add category
                                        setSearchOpen(false);
                                        setSearchQuery('');
                                        setEditingCategoryId(null);
                                        setEditingCategoryName('');
                                        setAddCategoryOpen(true);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 2,
                                        mb: 1,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <PlusIcon color={textHeadColor} />
                                    <Typography style={{ color: textHeadColor }}>{t('inventory.addCategory')}</Typography>
                                </Box>
                            )}

                            {generalError && (
                                <Typography color="error" variant="body2" sx={{ textAlign: 'center', marginTop: 1 }}>
                                    {generalError}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <SuccessNotification
                open={successNotification}
                message={t('inventory.categorySuccessMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)}
            />

            {/* Success Notification */}
            <SuccessNotification
                open={notificationOpen}
                message={successMessage}
                onClose={handleCloseNotification}
            />

            {/* Delete Confirmation Popup */}
            <DeleteConfirmationPopup
                open={isDeletePopupOpen}
                onClose={handleCloseDeletePopup}
                heading={t('inventory.deleteConfirmHeading1')}
                message={t('inventory.deleteConfirmMessage1')}
                onConfirm={handleConfirmDelete}
                itemName={`(${selectedCategory?.name})`}
                errorMessage={errorMessage}
            />
        </>
    );
}

export default CategoryModal;
